// General
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { observer, inject } from 'mobx-react';
import { Whitespace, Text, ViewBox } from '../common';
import Button from '../common/FormComponents/Button';
import CustomCheckbox from '../../components/CustomCheckbox/CustomCheckbox';
import { Flex, Box } from 'rebass';
import Link from '../../components/Link';
import ViewListing from '../../routes/viewListing/ViewListing';
import { colors } from '../../tailwind';
import SocialShare from '../../components/ViewListing/SocialShare/SocialShare';
import { observable } from 'mobx';
import Modal from '../Modal';
import Stepper from '../Stepper';
import EmailVerification from '../verification/EmailVerification';

@inject('userStore')
@observer
class Review extends Component {
  @observable modalList = [];
  @observable modalState = {
    closed: true,
  };

  static propTypes = {
    store: PropTypes.object,
    nextPage: PropTypes.func,
  };

  checkEmail = async () => {
    await this.props.userStore.refreshVerification();
    return this.props.userStore.isPhoneVerified;
  };

  showPhone = () => {
    this.modalState.closed = false;
  };

  endPhone = () => {
    this.modalState.closed = true;
  };

  componentWillMount() {
    const { userStore } = this.props;
    if (!userStore.isPhoneVerified) {
      this.modalList.push({
        required: true,
        interval: 6000,
        autocheck: async () => {
          return this.checkEmail();
        },
        render: next => (
          <EmailVerification
            next={this.endPhone}
            isOnboarding={false}
            userStore={this.props.userStore}
          />
        ),
      });
    }
  }

  renderPublished = () => {
    const {
      store: { listId, title },
      nextPage,
    } = this.props;

    return (
      <div>
        <Text bold primary header inline>
          You're published
        </Text>
        <Whitespace small />
        <Text noclip large gray>
          You don't need to do anything else. Your changes are live.
        </Text>

        <Whitespace />
        <Text bold primary header inline>
          Share it
        </Text>
        <Whitespace small />
        <SocialShare iconSize={32} listId={listId} title={title} />
        <Whitespace />

        <Link to={'/details/' + listId}>
          <Button primary action>
            View live listing
          </Button>
        </Link>

        <Whitespace smaller />

        <Text onClick={nextPage} link mt={4}>
          Unpublish this listing
        </Text>
      </div>
    );
  };

  renderUnpublished = () => {
    const {
      store,
      store: {
        listId,
        liabilityAccepted,
        termsAccepted,
        published,
        publishValidation,
      },
      nextPage,
      userStore: { isPhoneVerified },
    } = this.props;

    const hasErrors = !publishValidation.isValid;

    const cantPublish = published
      ? false
      : !termsAccepted || !liabilityAccepted || hasErrors;

    return (
      <div>
        <Text bold primary header inline>
          Liability
        </Text>
        <Whitespace small />
        <Text noclip large gray>
          Yoodlize has done everything it can to create a safe space for
          rentals. We can't do everything though, so please be sure you've taken
          any precautions you would like to protect yourself and your stuff
          before listing it. By checking below you are confirming that you
          understand and accept our terms of service.
        </Text>

        <Whitespace smaller />

        {!hasErrors && (
          <div>
            <CustomCheckbox
              name="terms"
              value={liabilityAccepted || published}
              checked={liabilityAccepted || published}
              onChange={event => (store.liabilityAccepted = event)}
            />
            <Text inline ml={3}>
              I accept the liability policy
            </Text>
          </div>
        )}

        <Whitespace />

        {hasErrors &&
          store.reviewSteps.map(stepName =>
            store.groupErrors(stepName).map(message => (
              <Flex alignItems="center">
                <span
                  style={{
                    color: colors.secondary,
                    marginLeft: '8px',
                    marginRight: '8px',
                  }}
                >
                  <i className="fas fa-times-circle" title={message} />
                </span>
                <span onClick={() => nextPage(stepName)}>
                  <Text large noclip link>
                    {message}
                  </Text>
                </span>
              </Flex>
            )),
          )}
        <Whitespace small />
        {!hasErrors && (
          <div>
            <CustomCheckbox
              name="terms"
              value={termsAccepted || published}
              checked={termsAccepted || published}
              onChange={event => (store.termsAccepted = event)}
            />
            <Text inline ml={3}>
              I agree to the{' '}
              <Link to="/policies">
                <Text clickable inline link>
                  Terms and conditions.
                </Text>
              </Link>
            </Text>
          </div>
        )}

        <Whitespace />

        {hasErrors && (
          <Text secondary>You shall not publish with errors to fix</Text>
        )}
        {isPhoneVerified && (
          <Button primary action disabled={cantPublish} onClick={nextPage}>
            Publish
          </Button>
        )}
        {!isPhoneVerified && (
          <div>
            <Text noclip large gray>
              You must verify your phone number before you can publish on
              Yoodlize. If you believe you've recieved this message in error,
              please contact support.
            </Text>
            <Button primary action onClick={this.showPhone}>
              Verify
            </Button>
          </div>
        )}
      </div>
    );
  };

  render() {
    const {
      store,
      store: { published, listId },
    } = this.props;

    const hasErrors = !store.publishValidation.isValid;

    return (
      <div className="mt-l">
        <div className="g g-tc-a-2 f-center-start g-g-lx">
          <Flex flexDirection="column" alignItems="center">
            <Text
              fontSize="5xl"
              thin
              primaryDark
              noclip
              letterSpacing="-.5px"
              style={{
                lineHeight: '48px',
              }}
              mb={4}
            >
              {hasErrors
                ? 'Almost there, tidy up to publish.'
                : published
                ? 'Changes saved.'
                : 'Review then publish.'}
            </Text>
            {listId && (
              <ViewListing
                listId={listId}
                preview
                style={{
                  transform: 'scale(.9)',
                }}
              />
            )}
          </Flex>
          <Box mx={4} mt="64px">
            <ViewBox
              flexDirection="column"
              className="p-l"
              noMin
              style={{ maxWidth: '300px' }}
            >
              <Box>
                <Text bold primary header inline>
                  Process Review
                </Text>
              </Box>

              <Whitespace small />

              <Text noclip large gray>
                Click{' '}
                <Link to="/policies">
                  <Text clickable inline link>
                    here
                  </Text>
                </Link>{' '}
                to learn more about reservations, payments, and more
              </Text>

              <Whitespace />

              {(published && this.renderPublished()) ||
                this.renderUnpublished()}
            </ViewBox>
          </Box>
        </div>
        <Modal isOpen={!this.modalState.closed}>
          <Stepper steps={this.modalList} onComplete={this.endPhone} />
        </Modal>
      </div>
    );
  }
}

export default Review;



// WEBPACK FOOTER //
// ./src/components_new/addListing/Review.js